import { usePublicServiceStore } from '@modules/service/m-public-service';

import { defineStore } from 'pinia';
import {
	generateKeyPair,
	decryptRSA,
	encryptAES,
	decryptAES,
	getAESKey,
	encryptAESGCM,
	decryptAESGCM,
	generateIV,
	getAESKeyGCM,
	CryptoProtocolError,
} from './cypher';
import MNoInternet from '@modals/m-no-internet';

export const useSecureStore = defineStore('m-secure', {
	// Estado reactivo
	state: () => ({
		baseURL:
			window.VITE_APP_CONFIG.endpoint ||
			import.meta.env.VITE_APP_ENDPOINT ||
			'https://api.grupocaminos.es',
		aesKey: null,
		aesKeyGCM: null,
		seed: null,
		symmetricKey: null,
		uuid: null,
		identity: null,
		noInternetModalOpen: false,
		// Auto-detection: v2 is tried first; if the backend rejects it, fallbackToV1()
		// pins v1 for the rest of the page session unless it also failed with v1.
		cryptoVersion: '2',
		cryptoV2Verified: false,
	}),

	getters: {
		getUuid() {
			return this.uuid;
		},
	},

	// Acciones
	actions: {
		// Establece los datos de la sesión
		setSessionData({ seed, symmetricKey, uuid }) {
			this.seed = seed;
			this.symmetricKey = symmetricKey;
			this.uuid = uuid;
			this.identity = Symbol(uuid);
			this.aesKey = null;
			this.aesKeyGCM = null;
		},

		// Limpia los datos de la sesión
		cleanSessionData() {
			this.seed = null;
			this.symmetricKey = null;
			this.uuid = null;
			this.identity = null;
			this.aesKey = null;
			this.aesKeyGCM = null;
			this.noInternetModalOpen = false;
		},

		// Resetea el flag del modal de sin internet para permitir reintentos
		resetNoInternetModal() {
			this.noInternetModalOpen = false;
		},

		// Confirms the backend accepted v2: disables the fallback for the rest of the page session
		markCryptoV2Verified() {
			this.cryptoV2Verified = true;
		},

		// Pins v1 (AES-CBC) as the protocol version for the rest of the page session
		fallbackToV1() {
			this.cryptoVersion = '1';
		},

		// Restores v2 (AES-GCM) when the v1 probe also failed: a business failure must
		// not leave the session degraded to v1
		restoreCryptoV2() {
			this.cryptoVersion = '2';
		},

		// Establece la clave AES
		setAESKey(key) {
			this.aesKey = key;
		},

		// Crea una sesión híbrida
		async createHybridSession() {
			return new Promise((resolve, reject) => {
				const channel = new MessageChannel();
				const requestTimer = setTimeout(() => {
					reject(new Error('TIMEOUT'));
				}, 10000);

				channel.port1.onmessage = ({ data }) => {
					clearTimeout(requestTimer);
					const isSuccess = data?.status === 200;
					if (isSuccess) {
						this.setSessionData({
							seed: data?.seed,
							uuid: data?.uuid,
							symmetricKey: data?.symmetricKey,
						});
						resolve(data);
					} else {
						reject(data);
					}
				};

				setTimeout(() => {
					window.postMessage(
						{
							name: 'native-get-secure-session',
							detail: {},
						},
						'*',
						[channel.port2]
					);
				}, 400);
			});
		},

		// Crea una nueva sesión con un nuevo uuid
		async createSession(secondTime = false) {
			// TODO(CD-17301): /key-exchange is assumed to return the same cryptographic material
			// (AES-256 key + seed) for both v1 (CBC) and v2 (GCM). if v2
			// ever requires its own key-exchange, the v1 fallback (m-service.request) should call
			// refreshSession() to recreate the session with the negotiated version.
			const { privateKey, publicKey } = await generateKeyPair();
			const url = '/key-exchange';
			const method = 'POST';
			const baseURL = this.baseURL;
			const headers = {
				'Content-Type': 'application/json',
				'public-key': publicKey,
			};
			//ENV Variable. If including withCredentials, will request OPTIONS calls. Is necessary to send cookie header
			const withCredentials = useAppStore().withCredentials;
			const publicService = usePublicServiceStore();
			return new Promise((resolve, reject) => {
				publicService
					.request({
						url,
						method,
						baseURL,
						headers,
						withCredentials,
					})
					.then(async (req) => {
						const { data } = req;
						if (data) {
							const response = await decryptRSA(privateKey, data);

							this.setSessionData(response);

							resolve(response);
						} else {
							reject(req);
						}
					})
					.catch(async (err) => {
						if (!secondTime) {
							await this.createSession(true)
								.then((data) => {
									this.noInternetModalOpen = false;
									resolve(data);
								})
								.catch(() => {
									if (!this.noInternetModalOpen) {
										this.noInternetModalOpen = true;
										useModalStore().open(MNoInternet);
									}
								});
						}
						reject(err);
					});
			});
		},

		// Elimina la sesión actual
		removeSession() {
			this.cleanSessionData();
		},

		// Refresca la sesión, creando una nueva y un nuevo uuid
		async refreshSession() {
			this.removeSession();
			return this.createSession();
		},

		// Devuelve la clave AES
		/*istanbul ignore next*/
		async getAESKey() {
			if (!this.uuid) {
				await this.createSession();
			}

			if (!this.aesKey) {
				const cryptoKey = await getAESKey(this.symmetricKey);
				this.setAESKey(cryptoKey);
				return cryptoKey;
			}

			return this.aesKey;
		},

		// Returns the imported key according to the cryptographic protocol version
		/*istanbul ignore next*/
		async getAESKeyForVersion() {
			if (this.cryptoVersion !== '2') {
				return this.getAESKey();
			}

			if (!this.uuid) {
				await this.createSession();
			}

			if (!this.aesKeyGCM) {
				const cryptoKey = await getAESKeyGCM(this.symmetricKey);
				this.aesKeyGCM = cryptoKey;
				return cryptoKey;
			}
			return this.aesKeyGCM;
		},

		// Encrypts data with the AES key (v1 CBC) or AES-GCM (v2)
		async encrypt(data) {
			if (this.cryptoVersion === '2') {
				const key = await this.getAESKeyForVersion();
				const iv = generateIV();
				const encrypted = await encryptAESGCM({ iv, key, data });

				return { encrypted, iv };
			}

			const key = await this.getAESKey();
			const { seed } = this;

			return encryptAES({
				seed,
				key,
				data,
			});
		},

		// Decrypts a base64 string with the AES key (v1 CBC) or AES-GCM (v2)
		async decrypt(data, responseIV) {
			if (this.cryptoVersion === '2') {
				if (!responseIV) {
					throw new CryptoProtocolError('MISSING_RESPONSE_IV');
				}
				const key = await this.getAESKeyForVersion();

				return decryptAESGCM({ iv: responseIV, key, data });
			}

			const key = await this.getAESKey();
			const { seed } = this;

			return decryptAES({
				seed,
				key,
				data,
			});
		},
	},
});
